/**
 * Swap a phone at its desk with a hand scanner (ADR 0049).
 *
 * One input, always focused: a keyboard-wedge scanner types the barcode and
 * sends Enter. The page tells a MAC (12 hex) from a serial by shape, so the
 * box label's two barcodes can be scanned in either order. Steps: scan the
 * old phone → confirm the desk (what the platform believes, and where the
 * phone last registered from) → scan the new MAC and serial → commit. A
 * moved desk ends with the 933 walk-through: the platform watches for the
 * emergency call attributed to the new phone (ADR 0041) and the installer
 * records the read-back. Everything here needs `devices.swap`.
 */

import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getE911Options,
  swapDevice,
  swapEvents,
  swapLookup,
  swapRecord933,
} from "@pbxmgr/api";
import { ApiError } from "@pbxmgr/api";
import type { SwapLookup, SwapResult } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";

const MAC_RE = /^[0-9a-f]{12}$/;

/** Normalise a scanned value to a MAC (12 lowercase hex) or null. */
export function asMac(raw: string): string | null {
  const hex = raw.replace(/[^0-9a-fA-F]/g, "").toLowerCase();
  return MAC_RE.test(hex) ? hex : null;
}

type Step = "old" | "confirm" | "new" | "done";

function errorText(err: unknown): string {
  return err instanceof ApiError
    ? (err.detail ?? err.message)
    : "request failed";
}

export function SwapPage(): ReactNode {
  const canSwap = usePermission("devices.swap");
  const [step, setStep] = useState<Step>("old");
  const [scan, setScan] = useState("");
  const [old, setOld] = useState<SwapLookup | null>(null);
  const [unchanged, setUnchanged] = useState(true);
  const [locationId, setLocationId] = useState("");
  const [newMac, setNewMac] = useState<string | null>(null);
  const [serial, setSerial] = useState<string | null>(null);
  const [result, setResult] = useState<SwapResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const options = useQuery({
    queryKey: ["e911", "options"],
    queryFn: getE911Options,
  });

  // The scanner needs the field focused at every step; refocus after each change.
  useEffect(() => {
    input.current?.focus();
  }, [step, old, newMac, serial]);

  const reset = (): void => {
    setStep("old");
    setScan("");
    setOld(null);
    setUnchanged(true);
    setLocationId("");
    setNewMac(null);
    setSerial(null);
    setResult(null);
    setError(null);
  };

  const lookup = useMutation({
    mutationFn: (mac: string) => swapLookup(mac),
    onSuccess: (found) => {
      setError(null);
      setOld(found);
      setLocationId(found.location_id ? String(found.location_id) : "");
      setStep("confirm");
    },
    onError: (err) => setError(errorText(err)),
  });

  const commit = useMutation({
    mutationFn: () =>
      swapDevice({
        old_mac: old?.mac ?? "",
        new_mac: newMac ?? "",
        serial,
        location_unchanged: unchanged,
        location_id: unchanged ? null : locationId ? Number(locationId) : null,
      }),
    onSuccess: (out) => {
      setError(null);
      setResult(out);
      setStep("done");
    },
    onError: (err) => setError(errorText(err)),
  });

  const onScan = (event: FormEvent): void => {
    event.preventDefault();
    const raw = scan.trim();
    setScan("");
    if (!raw) return;
    if (step === "old") {
      const mac = asMac(raw);
      if (!mac) {
        setError(
          "That does not look like a MAC — scan the old phone's MAC label.",
        );
        return;
      }
      lookup.mutate(mac);
    } else if (step === "new") {
      const mac = asMac(raw);
      if (mac) {
        if (mac === old?.mac) {
          setError("That is the old phone's MAC — scan the NEW unit's box.");
          return;
        }
        setNewMac(mac);
        setError(null);
      } else {
        setSerial(raw);
        setError(null);
      }
    }
  };

  if (!canSwap) {
    return (
      <div>
        <div className="dash__head">
          <h1>Swap a phone</h1>
        </div>
        <p className="ui-empty">devices.swap required.</p>
      </div>
    );
  }

  const canCommit =
    step === "new" &&
    newMac !== null &&
    (unchanged || locationId !== "") &&
    !commit.isPending;

  return (
    <div>
      <div className="dash__head">
        <h1>Swap a phone</h1>
        <span className="ui-hint">
          Scanner workflow (ADR 0049): old MAC → confirm the desk → new MAC +
          serial → commit.
        </span>
      </div>

      {step !== "done" ? (
        <Panel
          title={
            step === "old"
              ? "1. Scan the OLD phone's MAC"
              : step === "confirm"
                ? "2. Confirm this is the phone at this desk"
                : "3. Scan the NEW unit's MAC and serial"
          }
        >
          <form onSubmit={onScan} className="window-form">
            <input
              ref={input}
              aria-label="scanner input"
              autoFocus
              autoComplete="off"
              placeholder={
                step === "old"
                  ? "scan old MAC…"
                  : step === "new"
                    ? "scan new MAC, then serial…"
                    : "confirm below, then press Next"
              }
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              disabled={step === "confirm"}
            />
          </form>
          {lookup.isPending ? <Spinner /> : null}
          {error ? <Alert>{error}</Alert> : null}
        </Panel>
      ) : null}

      {old && step !== "done" ? (
        <Panel title={`Old phone — ${old.mac}`}>
          {old.state === "discovered_only" ? (
            <Alert>
              The nodes know this phone ({old.model},{" "}
              {old.extensions?.join(", ")}) but the platform does not yet.{" "}
              {old.hint}
            </Alert>
          ) : old.state === "retired" ? (
            <Alert>
              This phone was already replaced (successor device #
              {old.replaced_by_id}).
            </Alert>
          ) : (
            <>
              <p>
                <strong>{old.label ?? "unlabeled"}</strong> · {old.vendor}{" "}
                {old.model}
                {old.expansion_modules
                  ? ` + ${old.expansion_modules} sidecar`
                  : ""}{" "}
                ·{" "}
                <Badge tone={old.state === "adopted" ? "online" : "degraded"}>
                  {old.state}
                </Badge>
              </p>
              <p>
                Extensions:{" "}
                {old.lines && old.lines.length > 0
                  ? old.lines
                      .map((ln) => `${ln.extension}@${ln.node_name}`)
                      .join(", ")
                  : (old.inventory_extensions?.join(", ") ?? "none")}{" "}
                · buttons:{" "}
                {old.template_name
                  ? `template ${old.template_name}`
                  : "no template"}
                {old.device_buttons ? ` + ${old.device_buttons} own` : ""}
              </p>
              <p>
                Location: <strong>{old.location_name ?? "none"}</strong> ·{" "}
                {old.attested
                  ? `attested by ${old.attested_by}`
                  : "not attested"}{" "}
                ·{" "}
                <Badge
                  tone={old.e911_state === "verified" ? "online" : "degraded"}
                >
                  E911 {old.e911_state}
                </Badge>
              </p>
              <p>
                Last registered from: {old.registering_ip ?? "unknown"}
                {old.registering_in_range === true
                  ? " — inside this location's ranges"
                  : old.registering_in_range === false
                    ? " — OUTSIDE this location's ranges: check the desk"
                    : ""}
              </p>
              {step === "confirm" ? (
                <div className="window-form">
                  <label>
                    <input
                      type="checkbox"
                      checked={unchanged}
                      onChange={(e) => setUnchanged(e.target.checked)}
                    />{" "}
                    The phone stays at {old.location_name ?? "its location"} (no
                    location change — the new unit inherits the E911
                    attestation)
                  </label>
                  {!unchanged ? (
                    <div className="ui-field">
                      <label htmlFor="swap-location">
                        New location (a 933 test follows)
                      </label>
                      <select
                        id="swap-location"
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value)}
                      >
                        <option value="">— choose —</option>
                        {(options.data?.locations ?? []).map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  <Button
                    disabled={!unchanged && locationId === ""}
                    onClick={() => {
                      setError(null);
                      setStep("new");
                    }}
                  >
                    Next: scan the new unit
                  </Button>
                  <Button variant="secondary" onClick={reset}>
                    Wrong phone — start over
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Panel>
      ) : null}

      {step === "new" ? (
        <Panel title="New unit">
          <p>
            MAC: <strong>{newMac ?? "— scan —"}</strong> · serial:{" "}
            <strong>{serial ?? "— scan —"}</strong>
          </p>
          <div className="window-form">
            <Button disabled={!canCommit} onClick={() => commit.mutate()}>
              {commit.isPending ? "Swapping…" : "Swap now"}
            </Button>
            <Button variant="secondary" onClick={reset}>
              Start over
            </Button>
          </div>
          {!serial && newMac ? (
            <p className="ui-hint">
              Serial is optional — scan it for asset tracking, or swap now.
            </p>
          ) : null}
        </Panel>
      ) : null}

      {step === "done" && result ? (
        <SwapDone result={result} onNext={reset} />
      ) : null}
    </div>
  );
}

function SwapDone({
  result,
  onNext,
}: {
  result: SwapResult;
  onNext: () => void;
}): ReactNode {
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<string[]>([]);
  const events = useQuery({
    queryKey: ["swap", "events", result.successor_id],
    queryFn: () => swapEvents(result.successor_id),
    enabled: result.needs_933,
    refetchInterval: result.needs_933 ? 5000 : false,
  });
  const record = useMutation({
    mutationFn: (path: "primary" | "backup") =>
      swapRecord933(result.successor_id, {
        path,
        result: "ok",
        notes: notes || undefined,
      }),
    onSuccess: (_out, path) => {
      setError(null);
      setRecorded((prev) => [...prev, path]);
    },
    onError: (err) => setError(errorText(err)),
  });
  return (
    <>
      <Panel
        title={`Swapped — ${result.predecessor_mac} → ${result.successor_mac}`}
      >
        <p>
          New unit pre-registered
          {result.serial ? ` (serial ${result.serial})` : ""} at{" "}
          <strong>{result.location_name ?? "no location"}</strong> ·{" "}
          {result.lines.length} line(s) bound
          {result.lines.some((ln) => ln.rotated)
            ? " (SIP password rotated — the old phone is off the account)"
            : " (credentials carried over)"}{" "}
          · {result.buttons_copied} button(s) copied ·{" "}
          {result.attested ? (
            <Badge tone="online">E911 attestation inherited</Badge>
          ) : (
            <Badge tone="degraded">E911 not attested yet</Badge>
          )}
        </p>
        {result.notes.map((n) => (
          <Alert key={n}>{n}</Alert>
        ))}
        <p className="ui-hint">
          Plug the new phone in now. With its factory certificate it provisions
          on its first fetch with no window; it registers to both nodes within a
          minute.
        </p>
      </Panel>
      {result.needs_933 ? (
        <Panel title="933 walk-through (location changed)">
          <ol>
            <li>
              Wait for the new phone to register (the dashboard shows it).
            </li>
            <li>
              Dial <strong>933</strong> (never 911) from it. Listen for the
              address read back; it must be {result.location_name}&apos;s
              address. The platform shows the call below as it happens.
            </li>
            <li>
              Record the read-back for the home-node path. The fallback path is
              recorded by the operator when the home node is deliberately
              blocked.
            </li>
          </ol>
          <p>
            Observed calls:{" "}
            {events.data?.items.length
              ? events.data.items
                  .map(
                    (e) =>
                      `${e.observed_at ?? "?"} via ${e.node ?? "?"} → ${e.egress_ok === true ? "home node OK" : e.egress_ok === false ? "OFF HOME NODE" : "pending"}${e.connected === false ? " (not answered)" : ""}`,
                  )
                  .join("; ")
              : "none yet (refreshing every 5 s)"}
          </p>
          <div className="window-form">
            <input
              aria-label="933 notes"
              placeholder="address read back / notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button
              disabled={record.isPending || recorded.includes("primary")}
              onClick={() => record.mutate("primary")}
            >
              {recorded.includes("primary")
                ? "Home-node path recorded"
                : "Address read back OK (home node)"}
            </Button>
          </div>
          {error ? <Alert>{error}</Alert> : null}
        </Panel>
      ) : null}
      <div className="window-form">
        <Button onClick={onNext}>Next phone</Button>
      </div>
    </>
  );
}
